import React from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import type { UserFileItem, UserFileKind } from '../../../types/api';
import { SMART_SORTING_CATEGORIES } from '../../smart-sorting/lib/categories';

const ScrollContainer = styled.div`
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface);
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    padding: var(--space-md);
    text-align: left;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
  }

  th {
    color: var(--text-secondary);
    background: var(--surface-strong);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const FileName = styled.span`
  display: block;
  max-width: 300px;
  overflow-wrap: anywhere;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
`;

const Secondary = styled.span`
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  processing: 'Procesando',
  completed: 'Completado',
  failed: 'Error',
  active: 'Activo',
  expired: 'Vencido',
  archived: 'Archivado',
  draft: 'Borrador',
  confirmed: 'Confirmado',
  processed: 'Procesado',
  uploaded: 'Subido',
};

const KIND_LABELS: Record<UserFileKind, string> = {
  compliance: 'Cumplimiento',
  invoice: 'Factura',
  smart_sorting: 'Clasificación',
  report: 'Reporte',
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'medium',
  }).format(date);
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB`;
}

function getSmartSortingCategoryLabel(category: string | null): string | null {
  if (!category) return null;
  return (
    SMART_SORTING_CATEGORIES.find((config) => config.key === category)?.label ??
    null
  );
}

function getFileTypeLabel(item: UserFileItem): string {
  if (item.kind !== 'smart_sorting') return KIND_LABELS[item.kind];
  return (
    getSmartSortingCategoryLabel(item.category) ?? KIND_LABELS.smart_sorting
  );
}

export interface UserFilesTableProps {
  items: UserFileItem[];
  downloadingId: string | null;
  onDownload: (item: UserFileItem) => void;
}

export const UserFilesTable: React.FC<UserFilesTableProps> = ({
  items,
  downloadingId,
  onDownload,
}) => (
  <ScrollContainer role="region" aria-label="Archivos disponibles" tabIndex={0}>
    <Table>
      <caption className="sr-only">Archivos disponibles del usuario</caption>
      <thead>
        <tr>
          <th scope="col">Archivo</th>
          <th scope="col">Tipo</th>
          <th scope="col">Fecha</th>
          <th scope="col">Estado</th>
          <th scope="col">Tamaño</th>
          <th scope="col">
            <span className="sr-only">Acciones</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const isDownloading = downloadingId === item.id;
          const smartSortingCategoryLabel =
            item.kind === 'smart_sorting'
              ? getSmartSortingCategoryLabel(item.category)
              : null;
          const showCategory =
            item.category &&
            (item.kind !== 'smart_sorting' || !smartSortingCategoryLabel);
          return (
            <tr key={`${item.kind}-${item.id}`}>
              <td>
                <FileName title={item.name}>{item.name}</FileName>
                {showCategory && <Secondary>{item.category}</Secondary>}
              </td>
              <td>{getFileTypeLabel(item)}</td>
              <td>{formatDate(item.created_at)}</td>
              <td>
                {item.status
                  ? (STATUS_LABELS[item.status] ?? item.status)
                  : '—'}
              </td>
              <td>{formatSize(item.size_bytes)}</td>
              <td>
                <Actions>
                  <Button
                    variant="tertiary"
                    size="sm"
                    loading={isDownloading}
                    disabled={
                      !item.download_available ||
                      (downloadingId !== null && !isDownloading)
                    }
                    onClick={() => onDownload(item)}
                    startIcon={<Icon name="download" aria-hidden />}
                    aria-label={`Descargar ${item.name}`}
                  >
                    {isDownloading ? 'Descargando' : 'Descargar'}
                  </Button>
                </Actions>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  </ScrollContainer>
);
